import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bot, Send, Loader2, Sparkles, Trash2, X, ChevronDown, ChevronUp,
  FileText, User, Building2, Target, Lightbulb, Mail, MessageSquare,
  ClipboardCheck, AlertTriangle, Zap
} from 'lucide-react';
import { authAxios } from '../../services/crmApi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface AppContext {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  status: string;
  service_type: string;
  company_name?: string;
  business_idea_raw?: string;
  requested_amount?: number;
  dev_grant_project_title?: string;
  dev_grant_company_name?: string;
  dev_grant_company_size?: string;
  dev_grant_wojewodztwo?: string;
  created_at: string;
  updated_at?: string;
  [key: string]: any;
}

interface Props {
  application: AppContext;
  isOpen: boolean;
  onClose: () => void;
}

const CONTEXT_PROMPTS = [
  { label: 'Analiza wniosku', icon: Target, prompt: 'Przeanalizuj ten wniosek. Jakie widzisz mocne strony i ryzyka? Czy dokumentacja jest kompletna?' },
  { label: 'Kolejne kroki', icon: ClipboardCheck, prompt: 'Jakie powinny byc kolejne kroki dla tego klienta? Co powinienem zrobic jako operator?' },
  { label: 'Napisz maila', icon: Mail, prompt: 'Napisz profesjonalny email do tego klienta z aktualizacja statusu jego sprawy. Ton uprzejmy i rzeczowy.' },
  { label: 'Brakujace dokumenty', icon: FileText, prompt: 'Jakie dokumenty sa wymagane dla tego typu wniosku i ktore moga byc jeszcze potrzebne?' },
  { label: 'Ocena ryzyka', icon: AlertTriangle, prompt: 'Dokonaj oceny ryzyka tego wniosku. Czy sa jakies czerwone flagi? Na co zwrócić uwage?' },
  { label: 'Pomysl na biznes', icon: Lightbulb, prompt: 'Oceń pomysl na biznes tego klienta. Czy jest realny? Jakie sa szanse na sukces dotacji?' },
];

function buildContextSummary(app: AppContext): string {
  const parts: string[] = [
    `Wniosek #${app.id}: ${app.first_name} ${app.last_name}`,
    `Email: ${app.email}${app.phone ? `, Tel: ${app.phone}` : ''}`,
    `Produkt: ${app.service_type}`,
    `Status: ${app.status}`,
  ];
  if (app.company_name || app.dev_grant_company_name) {
    parts.push(`Firma: ${app.dev_grant_company_name || app.company_name}`);
  }
  if (app.dev_grant_project_title) parts.push(`Projekt: ${app.dev_grant_project_title}`);
  if (app.dev_grant_company_size) parts.push(`Wielkosc firmy: ${app.dev_grant_company_size}`);
  if (app.dev_grant_wojewodztwo) parts.push(`Wojewodztwo: ${app.dev_grant_wojewodztwo}`);
  if (app.business_idea_raw) parts.push(`Pomysl na biznes: ${app.business_idea_raw.substring(0, 200)}`);
  if (app.requested_amount) parts.push(`Kwota: ${Number(app.requested_amount).toLocaleString('pl-PL')} PLN`);
  parts.push(`Utwórzony: ${new Date(app.created_at).toLocaleDateString('pl-PL')}`);
  if (app.updated_at) parts.push(`Ostatnia aktualizacja: ${new Date(app.updated_at).toLocaleDateString('pl-PL')}`);
  return parts.join('\n');
}

export function OperatorAIChat({ application, isOpen, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const contextSummary = buildContextSummary(application);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
  }, []);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Reset when application changes
  useEffect(() => { setMessages([]); }, [application.id]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, userMsg].slice(-20).map(m => ({ role: m.role, content: m.content }));
      // Use the application-context AI chat endpoint
      const response = await authAxios.post('/api/ai/chat', {
        applicationId: application.id,
        message: text.trim(),
        history,
        context: contextSummary,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Przepraszam, nie udalo sie uzyskac odpowiedzi. Sprobuj ponownie.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-[440px] max-w-[calc(100vw-3rem)] bg-white shadow-2xl border-l z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-700 to-purple-800 text-white px-4 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <Sparkles size={18} className="text-amber-300" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">AI Asystent</h3>
              <p className="text-[10px] text-violet-200">Kontekst: #{application.id} {application.first_name} {application.last_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button onClick={() => setMessages([])} className="p-1.5 rounded-lg hover:bg-white/10 text-violet-300 hover:text-white" title="Wyczysc">
                <Trash2 size={14} />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-violet-300 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Context preview toggle */}
        <button
          onClick={() => setShowContext(!showContext)}
          className="mt-2 w-full flex items-center gap-2 text-[10px] text-violet-200 hover:text-white transition-colors"
        >
          <User size={10} />
          <span className="flex-1 text-left truncate">{application.service_type} | {application.status}</span>
          {showContext ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>

        {showContext && (
          <div className="mt-2 bg-black/20 rounded-lg p-3 text-[10px] text-violet-100 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
            {contextSummary}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center pt-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-3">
              <Bot size={24} className="text-white" />
            </div>
            <h4 className="text-base font-semibold text-gray-800 mb-1">Analiza wniosku #{application.id}</h4>
            <p className="text-xs text-gray-500 mb-4">
              {application.first_name} {application.last_name}
              {(application.company_name || application.dev_grant_company_name) &&
                ` | ${application.dev_grant_company_name || application.company_name}`}
            </p>

            <div className="grid grid-cols-2 gap-2">
              {CONTEXT_PROMPTS.map(qp => (
                <button
                  key={qp.label}
                  onClick={() => sendMessage(qp.prompt)}
                  className="text-left px-3 py-2.5 rounded-xl border border-violet-200 bg-violet-50/50 hover:bg-violet-100 hover:border-violet-400 text-xs text-violet-700 transition-all group flex items-start gap-2"
                >
                  <qp.icon size={13} className="text-violet-400 mt-0.5 shrink-0 group-hover:text-violet-600" />
                  <span>{qp.label}</span>
                </button>
              ))}
            </div>

            <div className="mt-4 bg-amber-50 rounded-lg p-3 text-[10px] text-amber-700 flex items-start gap-2">
              <Zap size={12} className="shrink-0 mt-0.5" />
              <span>AI widzi dane tego klienta — pytaj o szczegóły wniosku, pisz maile, analizuj ryzyko.</span>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center mr-2 mt-1 shrink-0">
                <Bot size={12} className="text-violet-600" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-violet-600 text-white rounded-br-md'
                : 'bg-gray-100 text-gray-800 rounded-bl-md'
            }`}>
              <div
                className="whitespace-pre-wrap break-words [&_strong]:font-semibold [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs"
                dangerouslySetInnerHTML={{
                  __html: msg.role === 'assistant'
                    ? msg.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/^- /gm, '• ')
                        .replace(/`(.*?)`/g, '<code>$1</code>')
                    : msg.content.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                }}
              />
              {msg.timestamp && (
                <p className={`text-[9px] mt-1 ${msg.role === 'user' ? 'text-violet-200' : 'text-gray-400'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center mr-2 shrink-0">
              <Bot size={12} className="text-violet-600" />
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-xs">Analizuje dane klienta...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="shrink-0 border-t p-3 bg-gray-50">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
            placeholder="Zapytaj AI o tego klienta..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white max-h-24"
            onInput={e => {
              const el = e.target as HTMLTextAreaElement;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 96) + 'px';
            }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="px-3 py-2.5 rounded-xl bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 transition-colors shrink-0"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
        <p className="text-[9px] text-gray-400 mt-1 text-center">Claude + RAG | Shift+Enter = nowa linia</p>
      </div>
    </div>
  );
}
